import { Injectable, computed, inject, signal } from '@angular/core';
import Keycloak from 'keycloak-js';

/**
 * Permission/role lookup utility — single source of truth for capability gates.
 *
 * Reads roles from the current Keycloak token (`realm_access.roles` and
 * `resource_access.<client>.roles`) without requiring a server round-trip.
 *
 * Usage:
 *   const perms = inject(PermissionService);
 *   if (perms.has('pos:access')) { ... }
 *   <button *ngIf="perms.has('pos:hold')">Hold</button>
 *
 * NOTE: Until the Keycloak `business` realm has the POS roles configured
 * (`pos:access`, `pos:discount`, `pos:hold`, `pos:return`, `pos:sell`),
 * `has()` returns `false` for them. The auth-guard treats an empty
 * `requiredKeys[]` (or non-configured keys) as "auth-only" — see
 * `auth-guard.ts` for the current rollout policy.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
    private readonly keycloak = inject(Keycloak, { optional: true });

    /** Reactive snapshot of all roles from the current token. */
    private readonly rolesSignal = signal<readonly string[]>(this.readRolesFromToken());

    /** Set-typed view used by `has()` for O(1) lookup. */
    private readonly roleSet = computed(() => new Set(this.rolesSignal()));

    /**
     * Refreshes the role cache. Call this on token refresh, tenant switch,
     * or after any keycloak event that mutates the token claims.
     */
    refresh(): void {
        this.rolesSignal.set(this.readRolesFromToken());
    }

    /** True if the current user has the given role/permission key. */
    has(key: string): boolean {
        return this.roleSet().has(key);
    }

    /** True if the user has every key in the list (logical AND). */
    hasAll(keys: readonly string[]): boolean {
        const roles = this.roleSet();
        return keys.every(k => roles.has(k));
    }

    /** True if the user has at least one key in the list (logical OR). */
    hasAny(keys: readonly string[]): boolean {
        const roles = this.roleSet();
        return keys.some(k => roles.has(k));
    }

    /** All roles currently in scope (read-only snapshot). */
    all(): readonly string[] {
        return this.rolesSignal();
    }

    private readRolesFromToken(): readonly string[] {
        const claims = this.keycloak?.tokenParsed as Record<string, unknown> | undefined;
        if (!claims) return [];

        const out: string[] = [];

        const realmAccess = claims['realm_access'] as { roles?: string[] } | undefined;
        if (realmAccess?.roles) out.push(...realmAccess.roles);

        const resourceAccess = claims['resource_access'] as
            | Record<string, { roles?: string[] }>
            | undefined;
        if (resourceAccess) {
            for (const k of Object.keys(resourceAccess)) {
                const roles = resourceAccess[k]?.roles;
                if (roles) out.push(...roles);
            }
        }

        // Some Keycloak setups expose a flat `permissions` claim.
        const permClaim = claims['permissions'];
        if (Array.isArray(permClaim)) out.push(...permClaim.filter((p): p is string => typeof p === 'string'));

        return out;
    }
}
